import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import { apiErrorMessage } from "../../utility/apiError";
import {
  Cart,
  CartItem,
  CartSummary,
  CartType,
  AddToCartRequest,
  BulkAddToCartRequest,
  UpdateCartItemRequest,
  ApplyReferralRequest,
  ApplyCouponRequest,
  ApplySalarCodeRequest,
  ApplyVoucherRequest,
  SelectDeliveryOptionRequest,
  SelectAddressRequest,
  ValidateCartRequest,
  CartState,
  ApiResponse,
  AvailableCartCoupon,
} from "./types";

const API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_PAYMENT_APP_BACKEND_PREFIX, // Prefix for PaymentServices
]
  .filter(Boolean)
  .join("");

// Axios instance with cookies support
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add Bearer token
api.interceptors.request.use(
  (config) => {
    const raw = localStorage.getItem("access_token") || getCookie("access_token");
    const token = raw && raw !== "undefined" && raw !== "null" && raw.length > 20 ? raw : null;
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

const initialState: CartState = {
  cart: null,
  carts: [],
  savedForLaterItems: [],
  summary: null,
  mlmItems: [],
  ecommerceItems: [],
  availableCoupons: [],
  availableCouponsLoading: false,
  loading: false,
  error: null,
  success: false,
  message: null,
};

// Async Thunks
export const getCart = createAsyncThunk(
  "cart/getCart",
  async (cartType: CartType | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<Cart>>("/cart", {
        params: { cartType },
        headers: { "Cache-Control": "no-cache" },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to fetch cart"),
      );
    }
  },
);

export const fetchAvailableCoupons = createAsyncThunk(
  "cart/fetchAvailableCoupons",
  async (cartType: CartType | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<AvailableCartCoupon[]>>(
        "/cart/available-coupons",
        { params: { cartType } },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to load coupons"),
      );
    }
  },
);

export const getAllCarts = createAsyncThunk(
  "cart/getAllCarts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<Cart[]>>("/cart/all");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to fetch all carts"),
      );
    }
  },
);

export const getCartSummary = createAsyncThunk(
  "cart/getCartSummary",
  async (cartType: CartType | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<CartSummary>>(
        "/cart/summary",
        {
          params: { cartType },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to fetch summary"),
      );
    }
  },
);

export const getMLMItems = createAsyncThunk(
  "cart/getMLMItems",
  async (_, { rejectWithValue }) => {
    try {
      const response =
        await api.get<ApiResponse<CartItem[]>>("/cart/mlm-items");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to fetch MLM items"),
      );
    }
  },
);

export const getEcommerceItems = createAsyncThunk(
  "cart/getEcommerceItems",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<CartItem[]>>(
        "/cart/ecommerce-items",
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to fetch E-commerce items"),
      );
    }
  },
);

export const addToCart = createAsyncThunk(
  "cart/addToCart",
  async (
    { data, cartType }: { data: AddToCartRequest; cartType?: CartType },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post<ApiResponse<Cart>>("/cart/add", data, {
        params: { cartType },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to add item to cart"),
      );
    }
  },
);

export const bulkAddToCart = createAsyncThunk(
  "cart/bulkAddToCart",
  async (data: BulkAddToCartRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<Cart>>(
        "/cart/bulk-add",
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to bulk add items"),
      );
    }
  },
);

export const updateCartItem = createAsyncThunk(
  "cart/updateCartItem",
  async (
    {
      itemId,
      data,
      cartType,
    }: { itemId: string; data: UpdateCartItemRequest; cartType?: CartType },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.put<ApiResponse<Cart>>(
        `/cart/update/${itemId}`,
        data,
        {
          params: { cartType },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to update item"),
      );
    }
  },
);

export const removeFromCart = createAsyncThunk(
  "cart/removeFromCart",
  async (
    { itemId, cartType }: { itemId: string; cartType?: CartType },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.delete<ApiResponse<Cart>>(
        `/cart/remove/${itemId}`,
        {
          params: { cartType },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to remove item"),
      );
    }
  },
);

export const clearCart = createAsyncThunk(
  "cart/clearCart",
  async (cartType: CartType | undefined, { rejectWithValue }) => {
    try {
      const response = await api.delete<ApiResponse<Cart>>("/cart/clear", {
        params: { cartType },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to clear cart"),
      );
    }
  },
);

export const applyReferral = createAsyncThunk(
  "cart/applyReferral",
  async (
    { data, cartType }: { data: ApplyReferralRequest; cartType?: CartType },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post<ApiResponse<Cart>>(
        "/cart/apply-referral",
        data,
        {
          params: { cartType },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to apply referral"),
      );
    }
  },
);

export const removeReferral = createAsyncThunk(
  "cart/removeReferral",
  async (cartType: CartType | undefined, { rejectWithValue }) => {
    try {
      const response = await api.delete<ApiResponse<Cart>>(
        "/cart/remove-referral",
        {
          params: { cartType },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to remove referral"),
      );
    }
  },
);

export const applyCoupon = createAsyncThunk(
  "cart/applyCoupon",
  async (
    { data, cartType }: { data: ApplyCouponRequest; cartType?: CartType },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post<ApiResponse<Cart>>(
        "/cart/apply-coupon",
        data,
        {
          params: { cartType },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to apply coupon"),
      );
    }
  },
);

export const removeCoupon = createAsyncThunk(
  "cart/removeCoupon",
  async (cartType: CartType | undefined, { rejectWithValue }) => {
    try {
      const response = await api.delete<ApiResponse<Cart>>(
        "/cart/remove-coupon",
        {
          params: { cartType },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to remove coupon"),
      );
    }
  },
);

export const applySalarCode = createAsyncThunk(
  "cart/applySalarCode",
  async (
    { data, cartType }: { data: ApplySalarCodeRequest; cartType?: CartType },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post<ApiResponse<Cart>>(
        "/cart/apply-salar-code",
        data,
        {
          params: { cartType },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to apply Salar code"),
      );
    }
  },
);

export const removeSalarCode = createAsyncThunk(
  "cart/removeSalarCode",
  async (cartType: CartType | undefined, { rejectWithValue }) => {
    try {
      const response = await api.delete<ApiResponse<Cart>>(
        "/cart/remove-salar-code",
        {
          params: { cartType },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to remove Salar code"),
      );
    }
  },
);

export const applyVoucher = createAsyncThunk(
  "cart/applyVoucher",
  async (
    { data, cartType }: { data: ApplyVoucherRequest; cartType?: CartType },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post<ApiResponse<Cart>>(
        "/cart/apply-voucher",
        data,
        {
          params: { cartType },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to apply voucher"),
      );
    }
  },
);

export const removeVoucher = createAsyncThunk(
  "cart/removeVoucher",
  async (cartType: CartType | undefined, { rejectWithValue }) => {
    try {
      const response = await api.delete<ApiResponse<Cart>>(
        "/cart/remove-voucher",
        {
          params: { cartType },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to remove voucher"),
      );
    }
  },
);

export const selectDeliveryOption = createAsyncThunk(
  "cart/selectDeliveryOption",
  async (
    {
      data,
      cartType,
    }: { data: SelectDeliveryOptionRequest; cartType?: CartType },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.put<ApiResponse<Cart>>(
        "/cart/delivery-option",
        data,
        {
          params: { cartType },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to select delivery option"),
      );
    }
  },
);

export const selectAddress = createAsyncThunk(
  "cart/selectAddress",
  async (
    { data, cartType }: { data: SelectAddressRequest; cartType: CartType },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.put<ApiResponse<Cart>>("/cart/address", data, {
        params: { cartType },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to select address"),
      );
    }
  },
);

export const validateCart = createAsyncThunk(
  "cart/validateCart",
  async (
    { data, cartType }: { data: ValidateCartRequest; cartType?: CartType },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post<
        ApiResponse<{ valid: boolean; errors: string[] }>
      >("/cart/validate", data, {
        params: { cartType },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to validate cart"),
      );
    }
  },
);

export const initCheckout = createAsyncThunk(
  "cart/initCheckout",
  async (cartType: CartType | undefined, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<Cart>>(
        "/cart/checkout/init",
        {},
        {
          params: { cartType },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to initialize checkout"),
      );
    }
  },
);

export const completeCart = createAsyncThunk(
  "cart/completeCart",
  async (cartId: string, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<Cart>>(
        `/cart/complete/${cartId}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to complete cart"),
      );
    }
  },
);

export const mergeCarts = createAsyncThunk(
  "cart/mergeCarts",
  async (guestSessionId: string, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<Cart>>("/cart/merge", {
        guestSessionId,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to merge carts"),
      );
    }
  },
);

export const getSavedForLater = createAsyncThunk(
  "cart/getSavedForLater",
  async (cartType: CartType | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<CartItem[]>>(
        "/cart/saved-for-later",
        { params: { cartType } },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to fetch saved items"),
      );
    }
  },
);

export const saveForLaterItem = createAsyncThunk(
  "cart/saveForLaterItem",
  async (
    { itemId, cartType }: { itemId: string; cartType?: CartType },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.put<ApiResponse<Cart>>(
        `/cart/items/${itemId}/save-for-later`,
        {},
        { params: { cartType } },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to save item for later"),
      );
    }
  },
);

export const moveSavedToCart = createAsyncThunk(
  "cart/moveSavedToCart",
  async (
    { itemId, cartType }: { itemId: string; cartType?: CartType },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.put<ApiResponse<Cart>>(
        `/cart/saved-for-later/${itemId}/move-to-cart`,
        {},
        { params: { cartType } },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to move item to cart"),
      );
    }
  },
);

export const removeSavedForLaterItem = createAsyncThunk(
  "cart/removeSavedForLaterItem",
  async (itemId: string, { rejectWithValue }) => {
    try {
      const response = await api.delete<ApiResponse<{ removed: boolean }>>(
        `/cart/saved-for-later/${itemId}`,
      );
      return { ...response.data, itemId };
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to remove saved item"),
      );
    }
  },
);

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    clearCartState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    const handlePending = (state: CartState) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    };

    const handleFulfilled = (
      state: CartState,
      action: PayloadAction<ApiResponse<Cart>>,
    ) => {
      state.loading = false;
      state.cart = state.cart
        ? { ...state.cart, ...action.payload.data }
        : action.payload.data;
      state.success = true;
      state.message = action.payload.message || null;
    };

    const handleRejected = (state: CartState, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = action.payload;
    };

    // getCart
    builder.addCase(getCart.pending, handlePending);
    builder.addCase(getCart.fulfilled, handleFulfilled);
    builder.addCase(getCart.rejected, handleRejected);

    builder.addCase(fetchAvailableCoupons.pending, (state) => {
      state.availableCouponsLoading = true;
    });
    builder.addCase(fetchAvailableCoupons.fulfilled, (state, action) => {
      state.availableCouponsLoading = false;
      state.availableCoupons = action.payload.data ?? [];
    });
    builder.addCase(fetchAvailableCoupons.rejected, (state) => {
      state.availableCouponsLoading = false;
      state.availableCoupons = [];
    });

    // getAllCarts
    builder.addCase(getAllCarts.pending, handlePending);
    builder.addCase(getAllCarts.fulfilled, (state, action) => {
      state.loading = false;
      state.carts = action.payload.data;
      state.success = true;
    });
    builder.addCase(getAllCarts.rejected, handleRejected);

    // getCartSummary
    builder.addCase(getCartSummary.pending, handlePending);
    builder.addCase(getCartSummary.fulfilled, (state, action) => {
      state.loading = false;
      state.summary = action.payload.data;
      state.success = true;
    });
    builder.addCase(getCartSummary.rejected, handleRejected);

    // getMLMItems
    builder.addCase(getMLMItems.pending, handlePending);
    builder.addCase(getMLMItems.fulfilled, (state, action) => {
      state.loading = false;
      state.mlmItems = action.payload.data;
      state.success = true;
    });
    builder.addCase(getMLMItems.rejected, handleRejected);

    // getEcommerceItems
    builder.addCase(getEcommerceItems.pending, handlePending);
    builder.addCase(getEcommerceItems.fulfilled, (state, action) => {
      state.loading = false;
      state.ecommerceItems = action.payload.data;
      state.success = true;
    });
    builder.addCase(getEcommerceItems.rejected, handleRejected);

    // addToCart
    builder.addCase(addToCart.pending, handlePending);
    builder.addCase(addToCart.fulfilled, handleFulfilled);
    builder.addCase(addToCart.rejected, handleRejected);

    // bulkAddToCart
    builder.addCase(bulkAddToCart.pending, handlePending);
    builder.addCase(bulkAddToCart.fulfilled, handleFulfilled);
    builder.addCase(bulkAddToCart.rejected, handleRejected);

    // updateCartItem
    builder.addCase(updateCartItem.pending, handlePending);
    builder.addCase(updateCartItem.fulfilled, handleFulfilled);
    builder.addCase(updateCartItem.rejected, handleRejected);

    // removeFromCart
    builder.addCase(removeFromCart.pending, handlePending);
    builder.addCase(removeFromCart.fulfilled, handleFulfilled);
    builder.addCase(removeFromCart.rejected, (state: CartState, action: PayloadAction<any>) => {
      state.loading = false;
      const errMsg = String(action.payload || "");
      if (!errMsg.toLowerCase().includes("not found")) {
        state.error = action.payload;
      }
    });

    // clearCart
    builder.addCase(clearCart.pending, handlePending);
    builder.addCase(clearCart.fulfilled, handleFulfilled);
    builder.addCase(clearCart.rejected, handleRejected);

    // applyReferral
    builder.addCase(applyReferral.pending, handlePending);
    builder.addCase(applyReferral.fulfilled, (state, action) => {
      state.loading = false;
      state.cart = action.payload.data;
      state.success = true;
      state.message = action.payload.message || null;
    });
    builder.addCase(applyReferral.rejected, handleRejected);

    // removeReferral
    builder.addCase(removeReferral.pending, handlePending);
    builder.addCase(removeReferral.fulfilled, handleFulfilled);
    builder.addCase(removeReferral.rejected, handleRejected);

    // applyCoupon
    builder.addCase(applyCoupon.pending, handlePending);
    builder.addCase(applyCoupon.fulfilled, handleFulfilled);
    builder.addCase(applyCoupon.rejected, handleRejected);

    // removeCoupon
    builder.addCase(removeCoupon.pending, handlePending);
    builder.addCase(removeCoupon.fulfilled, handleFulfilled);
    builder.addCase(removeCoupon.rejected, handleRejected);

    // applySalarCode
    builder.addCase(applySalarCode.pending, handlePending);
    builder.addCase(applySalarCode.fulfilled, handleFulfilled);
    builder.addCase(applySalarCode.rejected, handleRejected);

    // removeSalarCode
    builder.addCase(removeSalarCode.pending, handlePending);
    builder.addCase(removeSalarCode.fulfilled, handleFulfilled);
    builder.addCase(removeSalarCode.rejected, handleRejected);

    // applyVoucher
    builder.addCase(applyVoucher.pending, handlePending);
    builder.addCase(applyVoucher.fulfilled, handleFulfilled);
    builder.addCase(applyVoucher.rejected, handleRejected);

    // removeVoucher
    builder.addCase(removeVoucher.pending, handlePending);
    builder.addCase(removeVoucher.fulfilled, handleFulfilled);
    builder.addCase(removeVoucher.rejected, handleRejected);

    // selectDeliveryOption
    builder.addCase(selectDeliveryOption.pending, handlePending);
    builder.addCase(selectDeliveryOption.fulfilled, handleFulfilled);
    builder.addCase(selectDeliveryOption.rejected, handleRejected);

    // selectAddress
    builder.addCase(selectAddress.pending, handlePending);
    builder.addCase(selectAddress.fulfilled, handleFulfilled);
    builder.addCase(selectAddress.rejected, handleRejected);

    // validateCart
    builder.addCase(validateCart.pending, handlePending);
    builder.addCase(validateCart.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      // We could store validation result if needed, but for now just success
    });
    builder.addCase(validateCart.rejected, handleRejected);

    // initCheckout
    builder.addCase(initCheckout.pending, handlePending);
    builder.addCase(initCheckout.fulfilled, handleFulfilled);
    builder.addCase(initCheckout.rejected, handleRejected);

    // completeCart
    builder.addCase(completeCart.pending, handlePending);
    builder.addCase(completeCart.fulfilled, handleFulfilled);
    builder.addCase(completeCart.rejected, handleRejected);

    // mergeCarts
    builder.addCase(mergeCarts.pending, handlePending);
    builder.addCase(mergeCarts.fulfilled, handleFulfilled);
    builder.addCase(mergeCarts.rejected, handleRejected);

    // getSavedForLater
    builder.addCase(getSavedForLater.fulfilled, (state, action) => {
      state.savedForLaterItems = action.payload.data ?? [];
    });

    // saveForLaterItem — cart update + append newly flagged items to saved list
    builder.addCase(saveForLaterItem.pending, handlePending);
    builder.addCase(saveForLaterItem.fulfilled, (state, action) => {
      handleFulfilled(state, action);
      const newlySaved = (action.payload.data?.items ?? []).filter(
        (item) =>
          item.savedForLater &&
          !state.savedForLaterItems.some((saved) => saved.id === item.id),
      );
      state.savedForLaterItems = [
        ...state.savedForLaterItems,
        ...newlySaved,
      ];
    });
    builder.addCase(saveForLaterItem.rejected, handleRejected);

    // moveSavedToCart — cart update + drop the item from the saved list
    builder.addCase(moveSavedToCart.pending, handlePending);
    builder.addCase(moveSavedToCart.fulfilled, (state, action) => {
      handleFulfilled(state, action);
      state.savedForLaterItems = state.savedForLaterItems.filter(
        (item) => item.id !== action.meta.arg.itemId,
      );
    });
    builder.addCase(moveSavedToCart.rejected, handleRejected);

    // removeSavedForLaterItem
    builder.addCase(removeSavedForLaterItem.pending, handlePending);
    builder.addCase(removeSavedForLaterItem.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.savedForLaterItems = state.savedForLaterItems.filter(
        (item) => item.id !== action.meta.arg,
      );
    });
    builder.addCase(removeSavedForLaterItem.rejected, handleRejected);
  },
});

export const { clearCartState } = cartSlice.actions;
export default cartSlice.reducer;
